import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { CompanyListItem } from '@erp/rpc';

/** The `listCompanies` input every company picker sends. */
export function listCompaniesInput(
  forPricelistManagement: boolean,
  search: string,
) {
  return {
    page: 1,
    pageSize: 20,
    search: search || undefined,
    forPricelistManagement,
  };
}

export interface SelectedCompany {
  id: string;
  name: string;
  vendorNumbers?: string[];
}

export interface UseCompanyPickerOptions {
  /**
   * Item Master pricelist assignment. Asks `listCompanies` to collapse
   * division-scoped AS/400 vendor rows and lead the picker with vendor#.
   * Leave unset for EDI and other shared pickers.
   */
  forPricelistManagement?: boolean;
}

export interface UseCompanyPickerResult {
  selectedCompany: SelectedCompany | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearch: string;
  companies: CompanyListItem[];
  isLoading: boolean;
  selectCompany: (company: CompanyListItem | null) => void;
  reset: () => void;
  forPricelistManagement: boolean;
}

/**
 * Searchable company selection, shared by every flow that attaches a document
 * to the company it belongs to.
 *
 * Debounce lives here (not `useDebounce`) so `reset` can clear the in-flight
 * value immediately — otherwise reopening the picker would query the previous
 * search for 300ms.
 */
export function useCompanyPicker(
  /**
   * The company already chosen, when the picker is editing an existing
   * assignment rather than making a first one. `reset` returns here, so
   * abandoning a dialog leaves the record saying what it said before.
   */
  initialCompany: SelectedCompany | null = null,
  options: UseCompanyPickerOptions = {},
): UseCompanyPickerResult {
  const forPricelistManagement = options.forPricelistManagement === true;
  const queryClient = useQueryClient();
  const [selectedCompany, setSelectedCompany] =
    useState<SelectedCompany | null>(initialCompany);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!forPricelistManagement) return;
    void queryClient.prefetchQuery(
      orpc.core.companies.listCompanies.queryOptions({
        input: listCompaniesInput(true, ''),
      }),
    );
  }, [forPricelistManagement, queryClient]);

  const { data: companiesData, isFetching } = useQuery({
    ...orpc.core.companies.listCompanies.queryOptions({
      input: listCompaniesInput(forPricelistManagement, debouncedSearch),
    }),
    enabled: open,
  });

  const companies: CompanyListItem[] = companiesData?.companies ?? [];

  function selectCompany(company: CompanyListItem | null) {
    setSelectedCompany(
      company
        ? {
            id: company.id,
            name: company.name,
            ...(forPricelistManagement
              ? { vendorNumbers: company.vendorNumbers }
              : {}),
          }
        : null,
    );
    setOpen(false);
    setSearch('');
  }

  function reset() {
    setSelectedCompany(initialCompany);
    setOpen(false);
    setSearch('');
    setDebouncedSearch('');
  }

  return {
    selectedCompany,
    open,
    setOpen,
    search,
    setSearch,
    debouncedSearch,
    companies,
    isLoading: isFetching && companiesData === undefined,
    selectCompany,
    reset,
    forPricelistManagement,
  };
}
